/**
 * Bridge to the native Android media engine Capacitor plugin.
 * Supports two engines:
 *   • "exoplayer" (default for VOD)   — AndroidX Media3 / ExoPlayer
 *   • "vlc"       (default for Live)  — libVLC (VideoLAN)
 *
 * The user picks an engine PER CONTENT TYPE in PlayerSettings (Live TV /
 * Movies / Series). Defaults to ExoPlayer for all three buckets;
 * switch to VLC when a stream's codec ExoPlayer cannot handle.
 *
 *   localStorage keys:
 *     • zapp_engine_live    → "exoplayer" | "vlc"  (default "exoplayer")
 *     • zapp_engine_movie   → "exoplayer" | "vlc"  (default "exoplayer")
 *     • zapp_engine_series  → "exoplayer" | "vlc"  (default "exoplayer")
 *
 * Legacy key `zapp_player_engine` is migrated to all three the first time
 * we read it after the user upgrades from <= 1.1.9.
 */
import { isAndroid } from "./deviceId";

let plugin = null;
function getPlugin() {
  if (plugin) return plugin;
  if (!isAndroid()) return null;
  const Capacitor = window.Capacitor;
  if (!Capacitor) return null;
  try {
    plugin = Capacitor.Plugins?.NativePlayer || null;
  } catch {
    plugin = null;
  }
  return plugin;
}

export function isNativePlayerAvailable() {
  return !!getPlugin();
}

// v1.3.82 — Drain the last-known playback position that
// ExoPlayerActivity persisted to SharedPreferences before it was
// torn down. Called on every WebView `visibilitychange` → visible
// transition so Continue Watching's saved position stays in sync
// with the native activity's actual playhead. Best-effort — swallows
// errors so a plugin hiccup never blocks the UI.
//
// v1.0.9 — Also force-marks the item as watched when the native
// player exited ≤ 60 s from the end (or the SharedPreferences payload
// carries `endReached: true` from the ExoPlayer PlaybackListener).
// Previously we only relied on `saveHistoryPosition`'s internal
// auto-mark, which silently no-ops when ExoPlayer reports
// `durationMs = 0` (common for HLS / DASH streams without a header
// duration), leaving series episodes stuck without the ✓ badge even
// after the user finished them.
async function _drainNativePlaybackToHistory() {
  const p = getPlugin();
  if (!p || typeof p.drainLastPlayback !== "function") return;
  try {
    const r = await p.drainLastPlayback();
    if (!r || !r.url) return;
    const pos = Math.max(0, Math.floor((r.positionMs || 0) / 1000));
    const dur = Math.max(0, Math.floor((r.durationMs || 0) / 1000));
    if (pos < 3 && !r.endReached) return; // ignore trivial "just started" positions
    // Dynamic import to avoid a cycle with watchHistory → profiles.
    const wh = await import("./watchHistory");
    wh.saveHistoryPosition(r.url, pos, dur);
    // Belt-and-braces auto-mark. Trigger a mark when ANY of these are true:
    //   - Native side signalled `endReached: true`
    //   - `dur > 0` and remaining is ≤ 60 s (redundant with saveHistoryPosition's
    //     internal logic, but harmless — markWatched is idempotent)
    //   - `dur = 0` (unknown runtime, e.g. HLS) but the history entry
    //     already has a `duration_s` and we're within 60 s of it
    try {
      const wi = await import("./watchedItems");
      let shouldMark = false;
      let effectiveContentType = null;
      if (r.endReached) shouldMark = true;
      else if (dur > 0 && dur - pos <= 60) shouldMark = true;
      else if (dur === 0 && pos > 0) {
        // Fall back to the history entry's stored duration_s.
        try {
          const list = wh.getHistory ? wh.getHistory() : [];
          const prev = list.find((it) => it.stream_url === r.url);
          if (prev && Number.isFinite(prev.duration_s) && prev.duration_s > 0 &&
              prev.duration_s - pos <= 60) {
            shouldMark = true;
          }
          effectiveContentType = prev?.content_type || null;
        } catch { /* ignore */ }
      }
      if (shouldMark) wi.markWatched(r.url, effectiveContentType);
    } catch { /* watchedItems module unavailable — ignore */ }
  } catch { /* ignore */ }
}

// Wire the drain to visibilitychange + pageshow so we catch every
// way the WebView regains focus (Android backgrounds it while the
// native ExoPlayer activity is on top).
if (typeof document !== "undefined" && typeof window !== "undefined") {
  const onVis = () => {
    if (document.visibilityState === "visible") {
      _drainNativePlaybackToHistory();
    }
  };
  document.addEventListener("visibilitychange", onVis);
  window.addEventListener("pageshow", onVis);
  // Also fire once at boot in case the app was killed mid-playback
  // and the SharedPreferences entry outlives the process.
  _drainNativePlaybackToHistory();
}

// ---------- per-content-type engine preferences -----------------------------
const ENGINE_KEYS = {
  live: "zapp_engine_live",
  movie: "zapp_engine_movie",
  series: "zapp_engine_series",
};
// v1.4.30 — Per user choice (Option 2a) default ALL three buckets to
// ExoPlayer. Users can switch any bucket to libVLC from PlayerSettings
// if a channel/movie/episode's audio codec ExoPlayer cannot decode.
const ENGINE_DEFAULTS = {
  live: "exoplayer",
  movie: "exoplayer",
  series: "exoplayer",
};
const LEGACY_KEY = "zapp_player_engine";

function normalize(v) {
  return v && v.toLowerCase() === "vlc" ? "vlc" : "exoplayer";
}

/**
 * Map a Watch-page content-type token to one of our three engine buckets.
 *   "live_tv" | "catchup" → "live"
 *   "movie"               → "movie"
 *   "series"              → "series"
 *   (anything else)       → "movie"  (sensible default for ad-hoc URLs)
 */
export function bucketForContentType(contentType) {
  const t = (contentType || "").toLowerCase();
  if (t === "live_tv" || t === "live" || t === "catchup") return "live";
  if (t === "series" || t === "episode") return "series";
  return "movie";
}

/** Migrate the legacy single-engine pref into the three per-bucket keys.
 *  No-op once any of the new keys are present. */
function migrateLegacyEngine() {
  try {
    if (
      localStorage.getItem(ENGINE_KEYS.live) ||
      localStorage.getItem(ENGINE_KEYS.movie) ||
      localStorage.getItem(ENGINE_KEYS.series)
    ) {
      return;
    }
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (!legacy) return;
    const norm = normalize(legacy);
    localStorage.setItem(ENGINE_KEYS.live, norm);
    localStorage.setItem(ENGINE_KEYS.movie, norm);
    localStorage.setItem(ENGINE_KEYS.series, norm);
  } catch {
    /* localStorage may be unavailable */
  }
}

/**
 * Read the user-selected engine for a given content type. Returns
 * "exoplayer" or "vlc". Falls back to bucket-specific defaults when no
 * preference is stored (Live TV → vlc, Movies/Series → exoplayer).
 *
 * Call with no argument for backwards compatibility (returns the Movies
 * engine, matching old behaviour where everything used a single pref).
 */
export function getSelectedEngine(contentType) {
  try {
    migrateLegacyEngine();
    const bucket = bucketForContentType(contentType);
    const stored = localStorage.getItem(ENGINE_KEYS[bucket]);
    if (stored) return normalize(stored);
    return ENGINE_DEFAULTS[bucket];
  } catch {
    return ENGINE_DEFAULTS.movie;
  }
}

export function setSelectedEngine(bucket, value) {
  if (!ENGINE_KEYS[bucket]) return;
  try {
    localStorage.setItem(ENGINE_KEYS[bucket], normalize(value));
  } catch {
    /* ignore */
  }
}

/**
 * Launch the bundled native player activity full-screen.
 * Pass `autoPip: true` to make it dock into Picture-in-Picture as soon as
 * the first video frame is rendered (used by the Live TV "small screen +
 * EPG next to it" experience).
 * Returns { ok: true } on success, { ok: false, error } otherwise.
 */
export async function playWithNativeExoPlayer(url, { title, autoPip, engine, contentType, playlist, userAgent, channels, channelStartIndex, startPositionMs, posterUrl, rating, genre, releaseDate, channelGroup, currentEpg, nextEpg } = {}) {
  const p = getPlugin();
  if (!p) return { ok: false, error: "Native player plugin unavailable" };
  try {
    const r = await p.play({
      url,
      title: title || "",
      autoPip: !!autoPip,
      engine: engine || getSelectedEngine(contentType),
      // v1.3.98 — content_type drives which overlay style the native
      // ExoPlayerActivity draws on top of the Media3 PlayerView
      // controller. "movie" / "series" / "episode" → poster + title +
      // RATING/GENRE/DATE metadata line. "live_tv" → channel logo +
      // group + current/next EPG line + UP/DOWN zap chevrons.
      contentType: contentType || "",
      // Movies / Series metadata (VOD overlay).
      posterUrl: posterUrl || undefined,
      rating: Number.isFinite(rating) ? rating : undefined,
      genre: genre || undefined,
      releaseDate: releaseDate || undefined,
      // Live TV metadata. `currentEpg` / `nextEpg` shape:
      //   { title, start (ISO), stop (ISO) }
      channelGroup: channelGroup || undefined,
      currentEpg: currentEpg || undefined,
      nextEpg: nextEpg || undefined,
      // Optional follow-up queue. Each entry is
      // { stream_url, name?, logo_url? } — the native side just needs
      // stream_url; name/logo are reserved for future on-screen "Next:"
      // overlays.
      playlist: Array.isArray(playlist) ? playlist : undefined,
      // v1.3.81 — Optional Live-TV channel queue for native D-pad
      // Up/Down zapping. When provided, the ExoPlayer activity loads
      // the WHOLE list as a playlist and uses Media3's built-in
      // PreloadConfiguration to warm the next/previous channel so
      // remote-zap doesn't rebuffer. `channelStartIndex` is the
      // 0-based position of `url` inside `channels`.
      channels: Array.isArray(channels) && channels.length > 0 ? channels : undefined,
      channelStartIndex:
        Number.isFinite(channelStartIndex) && channelStartIndex >= 0
          ? channelStartIndex
          : undefined,
      // v1.3.82 — Continue Watching resume position, in milliseconds.
      // 0 or negative = start from beginning (default behaviour).
      startPositionMs:
        Number.isFinite(startPositionMs) && startPositionMs > 0
          ? Math.floor(startPositionMs)
          : undefined,
      // Admin-configured global User-Agent override — applied to the
      // HTTP request that opens the stream. Many IPTV providers gate
      // live channels by UA so this is the difference between
      // "channel won't play" and "channel plays". Falls back to the
      // ZAppUA global the bootstrap fetched from
      // /api/public/player-config.
      userAgent: userAgent || (typeof window !== "undefined" ? window.__ZAPP_UA__ : null) || undefined,
    });
    return { ok: r?.ok !== false, engine: r?.engine };
  } catch (e) {
    return { ok: false, error: e?.message || String(e) };
  }
}

/**
 * Mount an EMBEDDED native surface that sits over the WebView at the given
 * CSS-pixel rectangle. Used by the Live TV channel page so the live stream
 * renders inline alongside the EPG (no Picture-in-Picture needed).
 */
export async function playInlineNativeExoPlayer(url, rect, { title, engine, contentType, userAgent } = {}) {
  const p = getPlugin();
  if (!p?.playInline) return { ok: false, error: "Inline player unavailable" };
  try {
    const r = await p.playInline({
      url,
      title: title || "",
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
      engine: engine || getSelectedEngine(contentType),
      // Inline player also honours the admin UA override — same chain
      // as the full-screen path above.
      userAgent: userAgent || (typeof window !== "undefined" ? window.__ZAPP_UA__ : null) || undefined,
    });
    return { ok: r?.ok !== false, engine: r?.engine };
  } catch (e) {
    return { ok: false, error: e?.message || String(e) };
  }
}

export async function updateInlineNativeRect(rect) {
  const p = getPlugin();
  if (!p?.updateInlineRect) return;
  try {
    await p.updateInlineRect({
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
    });
  } catch {
    /* no-op */
  }
}

export async function stopInlineNativeExoPlayer() {
  const p = getPlugin();
  if (!p?.stopInline) return;
  try {
    await p.stopInline();
  } catch {
    /* no-op */
  }
}
